import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Sparkles, CalendarClock, Users, ScrollText, BookOpen,
  ImagePlus, FileCheck2, BrainCircuit, Library, MessagesSquare, ShieldCheck,
  Activity, CheckCircle2, Video, Send, Repeat, Mail, FileText, ChevronDown,
  Building2, GraduationCap, Bell, Smartphone, Eye, UserPlus, TrendingUp, Zap,
} from "lucide-react";
import { getStoredTheme, playPortalTransition } from "@/lib/theme";
import { PublicFooter } from "@/components/layout/PublicNav";
import FeatureShowcase from "@/components/landing/FeatureShowcase";
import { BookShelfStrip } from "@/pages/BookShelf";

/* ============================================================
   ATTIO-INSPIRED LANDING — Cream + Ink + Coral accent
   ============================================================ */

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: 0.05 * i, duration: 0.7, ease: [0.2, 0.7, 0.2, 1] },
  }),
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};

export default function Landing() {
  useEffect(() => {
    document.title = "TeachingSoftware.ai — The AI operating system for online teachers";
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute(
      "content",
      "TeachingSoftware.ai is the all-in-one platform for online tutors and coaching centres: live classes, AI quiz maker, AI lesson planner, paper checker, parent portal, billing — all in one."
    );
    const jsonld = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          name: "TeachingSoftware.ai",
          url: typeof window !== "undefined" ? window.location.origin : "",
          logo: typeof window !== "undefined" ? `${window.location.origin}/favicon.ico` : "",
          sameAs: ["https://www.cloudlabweb.com"],
          parentOrganization: { "@type": "Organization", name: "Cloud Lab Technologies", url: "https://www.cloudlabweb.com" },
        },
        {
          "@type": "WebSite",
          name: "TeachingSoftware.ai",
          url: typeof window !== "undefined" ? window.location.origin : "",
          potentialAction: {
            "@type": "SearchAction",
            target: `${typeof window !== "undefined" ? window.location.origin : ""}/p/{search_term_string}`,
            "query-input": "required name=search_term_string",
          },
        },
        {
          "@type": "SoftwareApplication",
          name: "TeachingSoftware.ai",
          applicationCategory: "EducationApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD", description: "Free trial — 100 AI credits, no card required" },
          featureList: ["Live classes", "AI quiz maker", "AI lesson planner", "AI paper checker", "Parent portal", "Class recordings", "Multi-teacher institute"],
        },
      ],
    };
    let script = document.getElementById("landing-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.id = "landing-jsonld";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonld);
    return () => {
      const s = document.getElementById("landing-jsonld");
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="bg-[#FBFAF7] text-[#0A0C10] antialiased overflow-x-hidden">
      <TopNav />
      <Hero />
      <LogoMarquee />
      <PillarOne />
      <PillarTwo />
      <PillarThree />
      <PillarFour />
      <BookShelfSection />
      <AvatarWall />
      <FinalCTA />
      <PublicFooter />
    </div>
  );
}

/* ---------------- BOOK SHELF SECTION ---------------- */
function BookShelfSection() {
  return (
    <section className="py-20 lg:py-28 bg-[#FBFAF7]" data-testid="landing-bookshelf">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
          <div className="max-w-xl">
            <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#FFB400] font-bold inline-flex items-center gap-2">
              <BookOpen size={12} /> Public book shelf
            </div>
            <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tighter mt-2">A curated shelf of textbooks, free to browse.</h2>
            <p className="text-neutral-600 mt-3 text-[15px] leading-relaxed">
              Our team curates a growing collection of textbooks, syllabi and study guides across grades and school systems. Tap any cover to read the PDF.
            </p>
          </div>
        </div>
        <BookShelfStrip />
      </div>
    </section>
  );
}

/* ---------------- NAV ---------------- */
function NeonToggle() {
  const [neon, setNeon] = useState(getStoredTheme() === "neon");
  const toggle = () => {
    const next = neon ? "cream" : "neon";
    playPortalTransition(next, () => setNeon(!neon));
  };
  return (
    <button
      onClick={toggle}
      data-testid="neon-theme-toggle"
      title={neon ? "Exit the neon portal" : "Enter futuristic neon mode"}
      className={`ts-neon-toggle ${neon ? "on" : ""}`}
    >
      <Zap size={13} className={neon ? "drop-shadow-[0_0_6px_rgba(34,211,238,0.9)]" : ""} />
      <span className="hidden sm:inline">{neon ? "Neon on" : "Neon"}</span>
    </button>
  );
}

function TopNav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <header data-testid="public-navbar" className={`sticky top-0 z-50 transition-all ${scrolled ? "bg-[#FBFAF7]/85 backdrop-blur-xl border-b border-black/5" : "bg-transparent border-b border-transparent"}`}>
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
          <div className="w-7 h-7 rounded-md bg-[#0A0C10] flex items-center justify-center">
            <span className="text-[#FBFAF7] font-display font-bold text-[11px]">TS</span>
          </div>
          <span className="font-display font-semibold tracking-tight text-[15px]">TeachingSoftware<span className="text-[#FFB400]">.ai</span></span>
        </Link>
        <nav className="hidden md:flex items-center gap-9 text-[13.5px] text-neutral-700">
          <a href="#platform" className="hover:text-black">Platform</a>
          <a href="#ai" className="hover:text-black">AI</a>
          <a href="#scale" className="hover:text-black">For scale</a>
          <a href="#pricing" className="hover:text-black">Pricing</a>
        </nav>
        <div className="flex items-center gap-3">
          <NeonToggle />
          <Link to="/login" data-testid="login-link" className="hidden sm:block text-[13.5px] font-medium hover:text-black/70">Sign in</Link>
          <Link to="/signup" data-testid="signup-cta" className="text-[13.5px] font-semibold bg-[#0A0C10] text-[#FBFAF7] px-4 py-2 rounded-full hover:bg-black transition-colors">Start for free →</Link>
        </div>
      </div>
    </header>
  );
}

/* ---------------- HERO ---------------- */
function Hero() {
  return (
    <section className="relative pt-10 sm:pt-16 lg:pt-24 pb-20 lg:pb-32">
      {/* Background grid */}
      <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.35]"
        style={{ backgroundImage: "linear-gradient(to right, rgba(10,12,16,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(10,12,16,0.06) 1px, transparent 1px)", backgroundSize: "48px 48px", maskImage: "radial-gradient(ellipse 80% 60% at 50% 40%, black 30%, transparent 80%)", WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 40%, black 30%, transparent 80%)" }} />

      {/* Soft glow */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[800px] h-[600px] rounded-full"
          style={{ background: "radial-gradient(circle at 50% 50%, var(--hero-glow-1, rgba(255,122,89,0.25)), transparent 60%)" }} />
        <div className="absolute top-40 right-0 w-[500px] h-[500px] rounded-full"
          style={{ background: "radial-gradient(circle, var(--hero-glow-2, rgba(0,47,167,0.12)), transparent 60%)" }} />
      </div>

      <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
        <motion.div initial="hidden" animate="show" variants={stagger} className="max-w-4xl">
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-white border border-black/8 rounded-full pl-1 pr-4 py-1 text-[12px] mb-8 shadow-[0_1px_2px_rgba(10,12,16,0.04)]">
            <span className="px-2 py-0.5 rounded-full bg-[#FFB400] text-white font-medium text-[11px]">New</span>
            <span className="text-neutral-700">AI Tutor that knows your syllabus</span>
            <ArrowRight size={12} className="text-neutral-500" />
          </motion.div>

          <motion.h1 variants={fadeUp} className="font-display font-semibold tracking-[-0.04em] text-[44px] sm:text-[64px] lg:text-[88px] leading-[0.95]">
            The teaching platform <br className="hidden sm:block" />
            engineered for <span className="italic font-medium" style={{ fontFamily: "'Instrument Serif', 'Outfit', serif" }}>online tutors.</span>
          </motion.h1>

          <motion.p variants={fadeUp} className="mt-7 max-w-2xl text-[17px] leading-[1.55] text-neutral-700">
            TeachingSoftware.ai is the AI teaching OS that builds your classes, generates quizzes, plans lessons, runs your live sessions and grows your tuition business — around the clock.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center gap-3">
            <Link to="/signup" data-testid="hero-start-free" className="group inline-flex items-center gap-2 bg-[#0A0C10] text-[#FBFAF7] font-semibold px-6 py-3.5 rounded-full text-[14px] hover:bg-black transition-all hover:scale-[1.02]">
              Start for free
              <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
            <button data-testid="hero-demo" className="inline-flex items-center gap-2 bg-white border border-black/10 px-6 py-3.5 rounded-full text-[14px] font-medium hover:border-black/30 transition-colors">
              Send me a demo
            </button>
            <Link to="/login" data-testid="hero-signin" className="hidden sm:inline-flex items-center gap-2 px-3 py-3.5 text-[14px] font-medium text-neutral-600 hover:text-black">
              Talk to sales
            </Link>
          </motion.div>
        </motion.div>

        {/* Hero feature showcase — replaces the previous static dashboard mockup */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
          className="mt-16"
        >
          <FeatureShowcase embedded />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 0.6 }}
          className="mt-16 flex items-center justify-center gap-2 text-neutral-500 text-[12px]"
        >
          <ChevronDown size={14} className="animate-bounce" /> Scroll to explore the platform
        </motion.div>
      </div>
    </section>
  );
}

/* Animated dashboard for hero */
function HeroDashboard() {
  const quizQuestions = [
    "Solve x² − 5x + 6 = 0",
    "Factorise: 2x² + 7x + 3",
    "What is the discriminant of ax² + bx + c?",
  ];
  const [typedIndex, setTypedIndex] = useState(0);
  const [typedText, setTypedText] = useState("");

  useEffect(() => {
    const full = quizQuestions[typedIndex];
    if (typedText.length < full.length) {
      const t = setTimeout(() => setTypedText(full.slice(0, typedText.length + 1)), 35);
      return () => clearTimeout(t);
    }
    const wait = setTimeout(() => {
      setTypedText("");
      setTypedIndex((i) => (i + 1) % quizQuestions.length);
    }, 1800);
    return () => clearTimeout(wait);
  }, [typedText, typedIndex]);

  return (
    <div className="relative mx-auto" style={{ perspective: "1800px" }}>
      <motion.div
        whileHover={{ rotateX: 0, rotateY: 0, scale: 1.01 }}
        initial={{ rotateX: 14, rotateY: -2 }}
        animate={{ rotateX: 8, rotateY: 0 }}
        transition={{ duration: 1.2, ease: [0.2, 0.7, 0.2, 1] }}
        className="relative rounded-2xl border border-black/10 bg-white shadow-[0_30px_80px_-20px_rgba(10,12,16,0.25),0_8px_24px_-8px_rgba(10,12,16,0.1)] overflow-hidden"
        style={{ transformStyle: "preserve-3d" }}
      >
        {/* Window chrome */}
        <div className="h-9 px-4 bg-[#F3F1ED] border-b border-black/8 flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-[#FF6058]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#FFBD2D]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#28C941]" />
          </div>
          <div className="ml-4 text-[11px] text-neutral-500 font-mono">teachingsoftware.ai / dashboard</div>
        </div>

        {/* Dashboard body */}
        <div className="grid grid-cols-12 min-h-[420px] sm:min-h-[480px]">
          {/* Sidebar */}
          <div className="hidden md:flex col-span-2 border-r border-black/8 bg-[#FAF9F6] flex-col py-4">
            <div className="px-4 mb-5">
              <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 font-mono">Workspace</div>
              <div className="mt-1 text-[13px] font-semibold">Ahmad&apos;s Academy</div>
            </div>
            <SidebarItem label="Overview" active />
            <SidebarItem label="Students" badge="42" />
            <SidebarItem label="Classes" badge="12" />
            <SidebarItem label="AI Quiz Maker" hot />
            <SidebarItem label="Lesson Plans" />
            <SidebarItem label="AI Images" />
            <SidebarItem label="Library" />
            <SidebarItem label="Billing" />
          </div>

          {/* Main content */}
          <div className="col-span-12 md:col-span-7 p-6 lg:p-8 border-r border-black/8">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-mono">/ overview</div>
                <h3 className="font-display font-semibold text-2xl tracking-tight mt-1">Hello, Ahmad.</h3>
              </div>
              <div className="text-[12px] bg-[#FFF4E5] text-[#A35200] px-2.5 py-1 rounded-full font-medium flex items-center gap-1">
                <Sparkles size={11} /> 2,847 credits
              </div>
            </div>

            {/* Stat tiles */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              <Tile k="Students" v="42" sub="+6 this week" />
              <Tile k="Classes today" v="3" sub="2:30 — 4:30" />
              <Tile k="Engagement" v="91%" sub="↑ 4%" green />
            </div>

            {/* AI Quiz live mockup */}
            <div className="rounded-xl border border-black/10 bg-gradient-to-br from-[#FFF7F3] to-white p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#FFB400] font-mono flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FFB400] animate-pulse" /> AI quiz maker · generating
                </div>
                <div className="text-[11px] text-neutral-500 font-mono">Math · Grade 9</div>
              </div>
              <div className="text-[15px] font-medium min-h-[28px]">
                {typedText}
                <span className="inline-block w-[2px] h-[16px] bg-[#0A0C10] ml-[1px] align-middle animate-pulse" />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {["A. (x−2)(x−3)", "B. (x+2)(x−3)", "C. (x−1)(x−6)", "D. (x+6)(x−1)"].map((o, i) => (
                  <motion.div
                    key={o}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: typedText.length > 6 ? 1 : 0, y: typedText.length > 6 ? 0 : 6 }}
                    transition={{ delay: 0.1 + i * 0.06 }}
                    className={`text-[12px] px-3 py-2 rounded-md border ${i === 0 ? "border-[#28C941]/40 bg-[#F1FBF3] text-[#0A6A1F]" : "border-black/10 bg-white"} flex items-center gap-1.5`}
                  >
                    {i === 0 && <CheckCircle2 size={11} className="text-[#28C941]" />} {o}
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Right rail */}
          <div className="col-span-12 md:col-span-3 p-5 bg-[#FAF9F6]">
            <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono mb-3">Upcoming class</div>
            <div className="rounded-xl border border-black/10 bg-white p-3">
              <div className="text-[12px] font-semibold">Algebra · Live</div>
              <div className="text-[11px] text-neutral-500 mt-0.5">14:00 — 15:00 · 18 students</div>
              <div className="mt-3 flex items-center justify-between">
                <div className="flex -space-x-1.5">
                  {[1,2,3,4].map((i) => <div key={i} className="w-5 h-5 rounded-full border-2 border-white" style={{ background: ["#FFB400","#002FA7","#FFD600","#28C941"][i-1] }} />)}
                </div>
                <button className="text-[11px] font-semibold bg-[#0A0C10] text-white rounded-full px-3 py-1">Join</button>
              </div>
            </div>
            <div className="mt-4 text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono mb-3">AI tutor</div>
            <div className="rounded-xl border border-black/10 bg-white p-3 space-y-2">
              <div className="text-[12px] bg-[#F3F1ED] rounded-lg px-2 py-1.5">How do I solve x² − 4 = 0?</div>
              <div className="text-[12px] bg-[#FFF4E5] rounded-lg px-2 py-1.5 leading-snug">
                Great question — this is a difference of squares...
                <span className="inline-block w-[1.5px] h-[10px] bg-[#0A0C10] ml-0.5 animate-pulse align-middle" />
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Floating cards around the dashboard */}
      <motion.div
        initial={{ opacity: 0, y: 20, x: -20 }} animate={{ opacity: 1, y: 0, x: 0 }} transition={{ delay: 1.0, duration: 0.6 }}
        className="hidden lg:block absolute -left-10 top-32 bg-white border border-black/10 rounded-xl shadow-[0_20px_40px_-10px_rgba(10,12,16,0.15)] p-3 w-56"
      >
        <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono">Post-class report</div>
        <div className="mt-1.5 text-[13px] font-semibold">Engagement <span className="text-[#28C941]">92%</span></div>
        <div className="text-[11px] text-neutral-500 mt-0.5">Weak topics: <span className="text-[#0A0C10]">quadratics, factoring</span></div>
        <div className="mt-2 h-1.5 bg-[#F3F1ED] rounded-full overflow-hidden">
          <motion.div className="h-full bg-[#28C941] rounded-full" initial={{ width: 0 }} animate={{ width: "92%" }} transition={{ delay: 1.4, duration: 1 }} />
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20, x: 20 }} animate={{ opacity: 1, y: 0, x: 0 }} transition={{ delay: 1.2, duration: 0.6 }}
        className="hidden lg:block absolute -right-10 bottom-24 bg-[#0A0C10] text-white rounded-xl shadow-[0_20px_40px_-10px_rgba(10,12,16,0.3)] p-3 w-60"
      >
        <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono flex items-center gap-1.5">
          <Sparkles size={10} className="text-[#FFD600]" /> AI lesson plan generated
        </div>
        <div className="mt-1.5 text-[13px] font-semibold">Algebra — 10-class plan</div>
        <div className="text-[11px] text-neutral-400 mt-0.5">Ready in 8 seconds.</div>
      </motion.div>
    </div>
  );
}

function SidebarItem({ label, active, badge, hot }) {
  return (
    <div className={`px-4 py-1.5 text-[12px] flex items-center justify-between ${active ? "bg-white text-[#0A0C10] font-medium border-l-2 border-[#FFB400]" : "text-neutral-600"}`}>
      <span>{label}</span>
      {badge && <span className="text-[10px] bg-black/5 px-1.5 rounded-full">{badge}</span>}
      {hot && <span className="text-[10px] bg-[#FFB400] text-white px-1.5 rounded-full">AI</span>}
    </div>
  );
}

function Tile({ k, v, sub, green }) {
  return (
    <div className="rounded-xl border border-black/8 bg-white p-3">
      <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 font-mono">{k}</div>
      <div className="font-display font-semibold text-2xl tracking-tight mt-1">{v}</div>
      <div className={`text-[11px] mt-0.5 ${green ? "text-[#28C941]" : "text-neutral-500"}`}>{sub}</div>
    </div>
  );
}

/* ---------------- LOGO MARQUEE ---------------- */
function LogoMarquee() {
  const items = ["TS Platform Live Class", "Google Meet", "Microsoft Teams", "Zoom", "Stripe", "OpenAI", "SendGrid", "WhatsApp", "Google Calendar"];
  const row = [...items, ...items];
  return (
    <section className="border-y border-black/8 bg-white py-10">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 font-mono text-center mb-6">
          Plug into the modern tutor stack
        </div>
        <div className="relative overflow-hidden" style={{ maskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)", WebkitMaskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)" }}>
          <motion.div
            className="flex gap-14 whitespace-nowrap"
            animate={{ x: ["0%", "-50%"] }}
            transition={{ repeat: Infinity, duration: 30, ease: "linear" }}
          >
            {row.map((it, i) => (
              <div key={i} className="font-display font-semibold text-[18px] text-neutral-700 hover:text-black transition-colors">{it}</div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- SECTION SHELL ---------------- */
function SectionLabel({ num, kicker, title, sub }) {
  return (
    <div className="grid grid-cols-12 gap-8 items-start mb-12">
      <div className="col-span-12 lg:col-span-5">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#FFB400] font-mono">[{num}] {kicker}</div>
        <h2 className="mt-4 font-display font-semibold tracking-[-0.035em] text-[36px] sm:text-[48px] lg:text-[60px] leading-[1.02]">{title}</h2>
      </div>
      <p className="col-span-12 lg:col-span-5 lg:col-start-8 text-[16.5px] leading-[1.55] text-neutral-700 lg:mt-12">{sub}</p>
    </div>
  );
}

/* ---------------- PILLAR 01 — AI that teaches with you ---------------- */
function PillarOne() {
  return (
    <section id="platform" className="py-24 lg:py-36">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        <SectionLabel
          num="01" kicker="POWERFUL PLATFORM"
          title={<>The intelligent tutor that <span className="italic font-medium" style={{ fontFamily: "'Instrument Serif', serif" }}>never sleeps.</span></>}
          sub="Picks up homework questions at 2am. Drafts the next lesson before your morning coffee. Flags weak topics before the parent does."
        />

        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger}
          className="grid grid-cols-12 gap-6"
        >
          <motion.div variants={fadeUp} className="col-span-12 lg:col-span-7 rounded-2xl border border-black/8 bg-white p-7 lg:p-10 relative overflow-hidden">
            <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-mono mb-4">AI generated · 4 seconds</div>
            <h3 className="font-display font-semibold text-[22px] lg:text-[26px] tracking-tight">Post-class report — Grade 9 · Algebra</h3>
            <div className="mt-6 space-y-3">
              <Stat row="Engagement rate" val="92%" pct={92} color="#28C941" />
              <Stat row="Confidence" val="Medium" pct={62} color="#FFD600" />
              <Stat row="Coverage of syllabus" val="On track" pct={78} color="#002FA7" />
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-3 text-[13px]">
              <div className="rounded-lg bg-[#F3F1ED] p-3"><div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono mb-1">Weak topics</div>Quadratic equations · Factoring</div>
              <div className="rounded-lg bg-[#F3F1ED] p-3"><div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono mb-1">Recommended next</div>Word problems with quadratics</div>
            </div>
            <div aria-hidden className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full" style={{ background: "radial-gradient(circle, rgba(255,122,89,0.18), transparent 70%)" }} />
          </motion.div>

          <motion.div variants={fadeUp} className="col-span-12 lg:col-span-5 grid grid-cols-1 gap-6">
            <div className="rounded-2xl bg-[#0A0C10] text-white p-7 relative overflow-hidden">
              <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-mono mb-3">AI assistant · live</div>
              <h3 className="font-display font-semibold text-[20px] tracking-tight">&ldquo;Generate 5 MCQs from this class.&rdquo;</h3>
              <div className="mt-5 space-y-1.5">
                <FakeBar w="92%" />
                <FakeBar w="78%" />
                <FakeBar w="84%" />
              </div>
              <button className="mt-5 text-[12px] bg-[#FFD600] text-[#0A0C10] font-semibold rounded-full px-4 py-2">Send to 18 students</button>
              <div aria-hidden className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-30" style={{ background: "radial-gradient(circle, #FFB400, transparent 60%)" }} />
            </div>

            <div className="rounded-2xl border border-black/8 bg-white p-7">
              <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-mono mb-3">AI image gen</div>
              <h3 className="font-display font-semibold text-[20px] tracking-tight">Infographic ready</h3>
              <div className="mt-4 aspect-[4/3] rounded-lg bg-gradient-to-br from-[#FFF4E5] via-[#FBFAF7] to-[#E8EEFF] flex items-center justify-center relative overflow-hidden">
                <div className="w-20 h-20 rounded-full bg-white shadow-[0_10px_30px_-5px_rgba(0,0,0,0.15)] flex items-center justify-center">
                  <ImagePlus size={28} className="text-[#FFB400]" />
                </div>
                <div className="absolute bottom-2 right-2 text-[10px] font-mono text-neutral-500 bg-white/80 backdrop-blur px-2 py-0.5 rounded">1024×1024 · 12s</div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function Stat({ row, val, pct, color }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[13px] mb-1.5">
        <span className="text-neutral-700">{row}</span>
        <span className="font-semibold font-mono text-[12px]">{val}</span>
      </div>
      <div className="h-2 bg-[#F3F1ED] rounded-full overflow-hidden">
        <motion.div className="h-full rounded-full" style={{ background: color }}
          initial={{ width: 0 }} whileInView={{ width: `${pct}%` }} viewport={{ once: true }}
          transition={{ duration: 1.1, ease: [0.2, 0.7, 0.2, 1] }} />
      </div>
    </div>
  );
}

function FakeBar({ w }) {
  return (
    <motion.div initial={{ width: 0 }} whileInView={{ width: w }} viewport={{ once: true }} transition={{ duration: 1, ease: "easeOut" }} className="h-1.5 bg-white/80 rounded-full" />
  );
}

/* ---------------- PILLAR 02 — For institutes & schools (parent-trust) ---------------- */
function PillarTwo() {
  return (
    <section id="ai" className="py-24 lg:py-36 bg-[#0A0C10] text-[#FBFAF7] relative overflow-hidden">
      {/* Ambient glows */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-[600px] h-[600px] rounded-full" style={{ background: "radial-gradient(circle, rgba(255,122,89,0.16), transparent 60%)" }} />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full" style={{ background: "radial-gradient(circle, rgba(255,214,0,0.10), transparent 60%)" }} />
      </div>

      <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="grid grid-cols-12 gap-8 items-start mb-12">
          <div className="col-span-12 lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#FFB400] font-mono">[02] FOR INSTITUTES & COACHING CENTRES</div>
            <h2 className="mt-4 font-display font-semibold tracking-[-0.035em] text-[36px] sm:text-[52px] lg:text-[68px] leading-[0.98]">
              Schools run smoother. <br />
              <span className="italic font-medium" style={{ fontFamily: "'Instrument Serif', serif" }}>Parents sleep better.</span>
            </h2>
          </div>
          <p className="col-span-12 lg:col-span-5 text-[16.5px] leading-[1.55] text-neutral-300 lg:mt-16">
            One operating system for the whole institute — dozens of teachers, hundreds of students, and the AI guardrails that let parents trust you with their child&apos;s day.
          </p>
        </div>

        {/* Live institute KPI strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10 mb-12"
        >
          {[
            { k: "Institutes onboarded", v: 240, suffix: "+", icon: Building2 },
            { k: "Active teachers", v: 4800, suffix: "+", icon: GraduationCap },
            { k: "Students taught monthly", v: 92000, suffix: "+", icon: Users },
            { k: "AI insights / week", v: 1200000, suffix: "+", icon: BrainCircuit },
          ].map((s, i) => (
            <KPITile key={i} {...s} delay={i * 0.1} />
          ))}
        </motion.div>

        {/* Institute admin dashboard mockup */}
        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
          className="rounded-2xl border border-white/10 bg-[#10141C] overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-md bg-[#FFD600] flex items-center justify-center">
                <Building2 size={16} className="text-[#0A0C10]" />
              </div>
              <div>
                <div className="font-display font-semibold text-[15px]">Greenfield International School</div>
                <div className="text-[10.5px] text-neutral-500 font-mono uppercase tracking-[0.15em]">Admin console · 14 Oct, 14:32</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 rounded-full bg-[#28C941]/15 text-[#28C941] text-[10px] font-mono uppercase tracking-[0.15em] flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#28C941] animate-pulse" /> All systems normal
              </span>
              <span className="px-2 py-1 rounded-full bg-white/5 text-neutral-300 text-[10px] font-mono">12 classes live now</span>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-px bg-white/10">
            {/* Teacher roster (left) */}
            <div className="col-span-12 lg:col-span-7 bg-[#10141C] p-6">
              <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono mb-3 flex items-center justify-between">
                <span>// teacher roster · live</span>
                <span className="inline-flex items-center gap-1 text-[#FFD600]"><UserPlus size={11} /> 3 onboarded this week</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {TEACHERS.map((t, i) => (
                  <TeacherRow key={t.name} t={t} delay={0.2 + i * 0.08} />
                ))}
              </div>
            </div>

            {/* Activity ticker (right) */}
            <div className="col-span-12 lg:col-span-5 bg-[#0A0C10] p-6">
              <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono mb-3">// live activity</div>
              <div className="space-y-2">
                {ACTIVITY.map((a, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                    transition={{ delay: 0.5 + i * 0.18, duration: 0.5 }}
                    className="flex items-start gap-2.5 text-[12px]"
                  >
                    <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: a.color }} />
                    <div className="flex-1">
                      <span className="text-neutral-300">{a.text}</span>
                      <span className="text-neutral-500 ml-1 font-mono text-[10px]">· {a.t}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Parent-trust feature cards */}
        <div className="mt-14 mb-2">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#FFD600] font-mono mb-2">// what parents see, what parents trust</div>
          <h3 className="font-display font-semibold tracking-[-0.025em] text-[28px] sm:text-[36px] leading-[1.05] max-w-2xl">
            Four reasons parents <span className="italic font-medium" style={{ fontFamily: "'Instrument Serif', serif" }}>actually answer your WhatsApp.</span>
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <TrustCard
            badge="Attendance"
            title={<>Every step <span className="italic" style={{ fontFamily: "'Instrument Serif', serif" }}>accounted for.</span></>}
            sub="Auto-marked the moment a student joins. Parents get a ping if a child is absent — before they ask."
            color="#28C941"
          >
            <AttendanceMockup />
          </TrustCard>

          <TrustCard
            badge="AI fallback"
            title={<>When teacher steps out, <span className="italic" style={{ fontFamily: "'Instrument Serif', serif" }}>AI steps in.</span></>}
            sub="Sick day, family emergency, traffic — your 24/7 AI tutor takes over so the class never stops."
            color="#FFB400"
          >
            <AIFallbackMockup />
          </TrustCard>

          <TrustCard
            badge="AI assessment"
            title={<>Weekly clarity, <span className="italic" style={{ fontFamily: "'Instrument Serif', serif" }}>delivered to parents.</span></>}
            sub="A Saturday morning digest with attendance, quiz scores, weak topics and AI&apos;s recommendation. Branded with your institute."
            color="#FFD600"
          >
            <ParentReportMockup />
          </TrustCard>

          <TrustCard
            badge="Concentration"
            title={<>Focus, <span className="italic" style={{ fontFamily: "'Instrument Serif', serif" }}>measured live.</span></>}
            sub="AI tracks attention every 30 seconds. Spot the kid who&apos;s slipping in week 2 — not week 12."
            color="#0DCAF0"
          >
            <ConcentrationMockup />
          </TrustCard>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="mt-14 rounded-2xl bg-gradient-to-br from-[#FFD600] to-[#FFB400] p-8 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 text-[#0A0C10]"
        >
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-mono opacity-70">// for principals & directors</div>
            <h3 className="mt-2 font-display font-semibold tracking-[-0.025em] text-[28px] sm:text-[36px] leading-[1.05]">
              Your institute deserves software <span className="italic" style={{ fontFamily: "'Instrument Serif', serif" }}>that thinks.</span>
            </h3>
            <p className="mt-2 text-[14.5px] max-w-xl opacity-80">Concierge onboarding · multi-teacher SSO · branded parent reports · dedicated success manager.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/signup" className="bg-[#0A0C10] text-[#FBFAF7] font-semibold px-6 py-3 rounded-full inline-flex items-center gap-2 text-[13.5px] hover:bg-black transition-colors">
              Book an institute demo <ArrowRight size={14} />
            </Link>
            <Link to="/login" className="bg-white/30 backdrop-blur border border-[#0A0C10]/10 px-6 py-3 rounded-full text-[13.5px] font-medium">Talk to sales</Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ---- Institute mock data ---- */
const TEACHERS = [
  { name: "Ahmad Raza",   subject: "Math · Grade 9–12",   live: true,  students: 84, color: "from-[#FFB400] to-[#FFD600]", initials: "AR" },
  { name: "Priya Sharma", subject: "Physics · Grade 11–12", live: true,  students: 62, color: "from-[#002FA7] to-[#0DCAF0]", initials: "PS" },
  { name: "John Martin",  subject: "English · Grade 6–10",  live: false, students: 71, color: "from-[#28C941] to-[#FFD600]", initials: "JM" },
  { name: "Layla Hassan", subject: "Biology · Grade 9–10",  live: true,  students: 53, color: "from-[#A82A6E] to-[#FFB400]", initials: "LH" },
  { name: "Vikram Singh", subject: "Chemistry · Grade 11", live: false, students: 47, color: "from-[#0DCAF0] to-[#28C941]", initials: "VS" },
  { name: "Mei Lin",      subject: "History · Grade 8–10",  live: false, students: 39, color: "from-[#FFD600] to-[#A82A6E]", initials: "ML" },
];

const ACTIVITY = [
  { text: "Ahmad Raza started Grade 9 Algebra · 18 joined",   t: "now",    color: "#28C941" },
  { text: "AI flagged 2 students with low focus in Physics",  t: "1 min",  color: "#FFD600" },
  { text: "Layla Hassan published a quiz · 53 assigned",      t: "3 min",  color: "#0DCAF0" },
  { text: "Saturday digest sent to 246 parents",              t: "1 hr",   color: "#FFB400" },
  { text: "Vikram Singh onboarded · 47 students migrated",    t: "3 hr",   color: "#28C941" },
  { text: "Mid-term AI assessment ready for Grade 10",        t: "5 hr",   color: "#FFD600" },
];

/* ---- Helpers ---- */
function KPITile({ k, v, suffix, icon: Icon, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ delay, duration: 0.5 }}
      className="bg-[#10141C] p-5 lg:p-6"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono">{k}</div>
        <Icon size={14} className="text-[#FFB400]" strokeWidth={1.5} />
      </div>
      <div className="mt-2 font-display font-semibold text-[32px] lg:text-[42px] tracking-[-0.035em] leading-none">
        <CountUp to={v} /><span className="text-[#FFD600]">{suffix}</span>
      </div>
    </motion.div>
  );
}

function CountUp({ to }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf, start;
    const dur = 1400;
    const animate = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(Math.floor(eased * to));
      if (p < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  return <>{v.toLocaleString()}</>;
}

function TeacherRow({ t, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
      transition={{ delay, duration: 0.5 }}
      className="rounded-xl bg-white/5 border border-white/5 p-3 flex items-center gap-3 hover:bg-white/8 transition-colors"
    >
      <div className={`relative w-10 h-10 rounded-full bg-gradient-to-br ${t.color} flex items-center justify-center text-[12px] font-display font-bold text-white shrink-0`}>
        {t.initials}
        {t.live && (
          <>
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#28C941] border-2 border-[#10141C]" />
            <motion.span
              className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#28C941]"
              animate={{ scale: [1, 1.8, 1], opacity: [0.6, 0, 0.6] }}
              transition={{ duration: 1.8, repeat: Infinity }}
            />
          </>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-semibold leading-tight truncate">{t.name}</div>
        <div className="text-[10.5px] text-neutral-500 mt-0.5 truncate">{t.subject}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-[11px] font-mono font-medium text-[#FFD600]">{t.students}</div>
        <div className="text-[9.5px] text-neutral-500 uppercase tracking-[0.15em] font-mono">students</div>
      </div>
    </motion.div>
  );
}

function TrustCard({ badge, title, sub, color, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
      className="group relative rounded-2xl border border-white/10 bg-[#10141C] overflow-hidden hover:border-white/20 transition-colors"
    >
      <div className="p-7">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded-full" style={{ background: `${color}22`, color }}>
          <span className="w-1 h-1 rounded-full" style={{ background: color }} /> {badge}
        </div>
        <h4 className="mt-4 font-display font-semibold text-[24px] tracking-[-0.025em] leading-[1.1]">{title}</h4>
        <p className="mt-2 text-[13.5px] text-neutral-400 leading-relaxed max-w-md">{sub}</p>
      </div>
      <div className="px-7 pb-7">{children}</div>
    </motion.div>
  );
}

/* ---- Trust mockups ---- */
function AttendanceMockup() {
  const students = [
    { n: "Eesa A.",    s: "in", t: "13:58" },
    { n: "Mohib K.",   s: "in", t: "13:59" },
    { n: "John M.",    s: "in", t: "14:00" },
    { n: "Vidya R.",   s: "in", t: "14:00" },
    { n: "Annie Z.",   s: "in", t: "14:01" },
    { n: "Sara H.",    s: "absent", t: "—" },
  ];
  return (
    <div className="rounded-xl bg-[#0A0C10] border border-white/8 overflow-hidden">
      <div className="px-3 py-2 flex items-center justify-between border-b border-white/5">
        <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-neutral-500">Grade 9 · Algebra · 14:02</div>
        <div className="text-[10px] font-mono text-[#28C941]">5 / 6 present</div>
      </div>
      <div className="divide-y divide-white/5">
        {students.map((st, i) => (
          <motion.div
            key={st.n}
            initial={{ opacity: 0, x: -8 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.3 + i * 0.12, duration: 0.4 }}
            className="px-3 py-2 flex items-center gap-2 text-[12px]"
          >
            <motion.div
              initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }}
              transition={{ delay: 0.5 + i * 0.12, type: "spring", stiffness: 260 }}
              className={`w-4 h-4 rounded-full flex items-center justify-center ${st.s === "in" ? "bg-[#28C941]" : "bg-[#FF6058]"}`}
            >
              {st.s === "in" ? <CheckCircle2 size={11} className="text-white" /> : <span className="text-white text-[9px] font-bold">!</span>}
            </motion.div>
            <span className={`flex-1 ${st.s === "absent" ? "text-neutral-500" : "text-neutral-200"}`}>{st.n}</span>
            <span className="text-[10px] font-mono text-neutral-500">{st.t}</span>
          </motion.div>
        ))}
      </div>
      <motion.div
        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 1.6 }}
        className="px-3 py-2 bg-[#FF6058]/10 border-t border-[#FF6058]/20 text-[11px] text-[#FF8B85] flex items-center gap-1.5"
      >
        <Bell size={11} /> Parent of Sara H. notified — auto-WhatsApp sent.
      </motion.div>
    </div>
  );
}

function AIFallbackMockup() {
  return (
    <div className="rounded-xl bg-[#0A0C10] border border-white/8 p-4 space-y-3">
      <div className="flex items-center gap-3">
        <div className="relative">
          <div className="w-9 h-9 rounded-full bg-neutral-700" />
          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-neutral-500 border-2 border-[#0A0C10]" />
        </div>
        <div className="flex-1">
          <div className="text-[12.5px] font-semibold flex items-center gap-2">
            Mr. Khan <span className="text-[9.5px] font-mono uppercase tracking-[0.15em] text-neutral-500 bg-white/5 px-1.5 py-0.5 rounded">offline · 14:05</span>
          </div>
          <div className="text-[10.5px] text-neutral-500">Traffic delay · expected at 14:25</div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.5 }}
        className="flex items-center justify-center"
      >
        <motion.div
          animate={{ y: [0, 4, 0] }} transition={{ duration: 1.4, repeat: Infinity }}
          className="text-neutral-500"
        >↓</motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        transition={{ delay: 0.9, duration: 0.5 }}
        className="flex items-center gap-3 rounded-lg bg-gradient-to-br from-[#FFB400]/15 to-[#FFD600]/10 border border-[#FFB400]/30 p-2"
      >
        <div className="relative w-9 h-9 rounded-full bg-gradient-to-br from-[#FFB400] to-[#FFD600] flex items-center justify-center">
          <Sparkles size={14} className="text-white" />
          <motion.span className="absolute inset-0 rounded-full border-2 border-[#FFD600]"
            animate={{ scale: [1, 1.3, 1], opacity: [0.7, 0, 0.7] }} transition={{ duration: 1.6, repeat: Infinity }} />
        </div>
        <div className="flex-1">
          <div className="text-[12.5px] font-semibold flex items-center gap-2">
            AI Tutor <span className="text-[9.5px] font-mono uppercase tracking-[0.15em] bg-[#28C941]/20 text-[#28C941] px-1.5 py-0.5 rounded">live · taking class</span>
          </div>
          <div className="text-[10.5px] text-neutral-400">Knows the syllabus · knows every student</div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 1.3 }}
        className="rounded-lg bg-white/5 p-2.5 text-[11.5px] text-neutral-300 leading-relaxed"
      >
        <span className="text-neutral-500">[AI · 14:06]</span> Hi everyone, Mr. Khan is on the way. While we wait, let&apos;s review yesterday&apos;s quadratic. Eesa, can you try (x−2)(x−3) = 0?
      </motion.div>
    </div>
  );
}

function ParentReportMockup() {
  return (
    <div className="flex justify-center">
      {/* Phone frame */}
      <div className="relative w-[230px] rounded-[28px] border-[7px] border-[#1a1f29] bg-[#0A0C10] shadow-[0_20px_50px_-15px_rgba(0,0,0,0.6)] overflow-hidden">
        <div className="h-5 bg-[#0A0C10] flex items-center justify-center">
          <div className="w-14 h-1 rounded-full bg-[#1a1f29]" />
        </div>
        <div className="bg-white text-[#0A0C10] px-3 py-3 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-[#FFD600] flex items-center justify-center"><Building2 size={13} className="text-[#0A0C10]" /></div>
            <div>
              <div className="text-[10px] font-semibold leading-tight">Greenfield International</div>
              <div className="text-[9px] text-neutral-500 font-mono">Sat · 09:00</div>
            </div>
          </div>
          <div className="text-[11px] font-display font-semibold leading-tight">
            Annie&apos;s week, in a glance.
          </div>
          <div className="space-y-1.5">
            <ReportRow label="Attendance" value="5 / 5" color="#28C941" />
            <ReportRow label="Quiz average" value="8.4 / 10" color="#28C941" />
            <ReportRow label="Focus score" value="86%" color="#FFD600" />
            <ReportRow label="Weak topic" value="Trig identities" color="#FFB400" />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 6 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.8 }}
            className="rounded-md bg-[#FFF4E5] border border-[#FFD600]/40 p-2 text-[9.5px] leading-relaxed"
          >
            <span className="font-semibold text-[#A35200]">AI tip:</span> 10 mins on trig identities this weekend would lock in next week&apos;s test prep.
          </motion.div>
          <div className="text-[9px] text-center text-neutral-500 font-mono pt-1">— sent to 246 parents at 09:00 IST</div>
        </div>
      </div>
    </div>
  );
}

function ReportRow({ label, value, color }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -6 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="flex items-center justify-between text-[10.5px]"
    >
      <span className="text-neutral-500">{label}</span>
      <span className="font-semibold" style={{ color }}>{value}</span>
    </motion.div>
  );
}

function ConcentrationMockup() {
  const rows = [
    { n: "Eesa A.",  f: 94, c: "#28C941" },
    { n: "Mohib K.", f: 71, c: "#FFD600" },
    { n: "John M.",  f: 38, c: "#FF6058" },
    { n: "Vidya R.", f: 88, c: "#28C941" },
  ];
  return (
    <div className="rounded-xl bg-[#0A0C10] border border-white/8 p-4 grid grid-cols-3 gap-4">
      {/* Gauge */}
      <div className="col-span-1 flex flex-col items-center justify-center">
        <div className="relative w-20 h-20">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle cx="50" cy="50" r="42" fill="none" stroke="#1a1f29" strokeWidth="9" />
            <motion.circle
              cx="50" cy="50" r="42" fill="none" stroke="url(#cGrad)" strokeWidth="9" strokeLinecap="round"
              pathLength={1}
              initial={{ strokeDasharray: 1, strokeDashoffset: 1 }}
              whileInView={{ strokeDashoffset: 1 - 0.73 }} viewport={{ once: true }}
              transition={{ duration: 1.4, ease: [0.2, 0.7, 0.2, 1] }}
            />
            <defs>
              <linearGradient id="cGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#28C941" /><stop offset="100%" stopColor="#FFD600" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="font-display font-bold text-xl tracking-tight">73%</div>
          </div>
        </div>
        <div className="text-[9.5px] uppercase tracking-[0.18em] text-neutral-500 font-mono mt-1.5">avg focus</div>
      </div>

      {/* Bars */}
      <div className="col-span-2 space-y-2">
        {rows.map((r, i) => (
          <div key={r.n}>
            <div className="flex items-center justify-between text-[10.5px]">
              <span className="text-neutral-300">{r.n}</span>
              <span className="font-mono font-semibold" style={{ color: r.c }}>{r.f}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/8 overflow-hidden mt-1">
              <motion.div
                initial={{ width: 0 }} whileInView={{ width: `${r.f}%` }} viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.3 + i * 0.08 }}
                className="h-full rounded-full"
                style={{ background: r.c }}
              />
            </div>
          </div>
        ))}
        <motion.div
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 1.4 }}
          className="text-[10px] text-[#FF8B85] font-mono pt-1 inline-flex items-center gap-1"
        >
          <Eye size={10} /> John flagged · 2-min focus drop
        </motion.div>
      </div>
    </div>
  );
}

/* ---------------- PILLAR 03 — Universal context ---------------- */
function PillarThree() {
  return (
    <section className="py-24 lg:py-36 bg-[#FBFAF7]">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        <SectionLabel
          num="03" kicker="UNIVERSAL CONTEXT"
          title={<>One layer for <span className="italic font-medium" style={{ fontFamily: "'Instrument Serif', serif" }}>every student.</span></>}
          sub="Attendance, quiz scores, weak topics, fees, parent contact — every signal compounds into a living profile your AI tutor can act on."
        />

        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger}
          className="grid grid-cols-12 gap-6 items-stretch"
        >
          <motion.div variants={fadeUp} className="col-span-12 lg:col-span-5 rounded-2xl border border-black/8 bg-white p-7 relative">
            <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-mono mb-3">Student profile</div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FFB400] to-[#FFD600]" />
              <div>
                <div className="font-display font-semibold text-[18px]">Annie Zhang</div>
                <div className="text-[12px] text-neutral-500">Grade 9 · Math + Physics · 4-month student</div>
              </div>
            </div>
            <div className="mt-6 space-y-2 text-[13px]">
              <KV k="Attendance" v="94%" />
              <KV k="Avg quiz score" v="8.4 / 10" />
              <KV k="Weak topics" v="Quadratics · Trig identities" />
              <KV k="Last fee" v="Paid · Oct 1" />
              <KV k="Parent" v="zhang.fam@email.com" />
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <Tag>Visual learner</Tag><Tag>Loves word problems</Tag><Tag>Likes 9 PM slot</Tag>
            </div>
          </motion.div>

          <motion.div variants={fadeUp} className="col-span-12 lg:col-span-7 rounded-2xl border border-black/8 bg-white p-7">
            <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-mono mb-3">Workflow · Sept → Oct</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <WorkflowNode i={1} title="New lead from Google" body="Ahmad's public profile · ‘math tutor grade 9’" icon={Users} />
              <WorkflowNode i={2} title="Demo class booked" body="AI demo playbook auto-generated" icon={CalendarClock} />
              <WorkflowNode i={3} title="Enrolled · weekly Mon+Wed" body="Auto-email + WhatsApp to parent" icon={Mail} />
              <WorkflowNode i={4} title="AI runs after every class" body="Engagement, weak topics, homework" icon={BrainCircuit} highlight />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex items-center justify-between border-b border-dashed border-black/10 pb-1.5">
      <span className="text-neutral-500">{k}</span><span className="font-medium">{v}</span>
    </div>
  );
}
function Tag({ children }) {
  return <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F3F1ED]">{children}</span>;
}
function WorkflowNode({ i, title, body, icon: Icon, highlight }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
      transition={{ delay: i * 0.08, duration: 0.5 }}
      className={`rounded-xl p-4 ${highlight ? "bg-[#0A0C10] text-white" : "bg-[#FBFAF7] border border-black/8"}`}
    >
      <div className="flex items-center gap-2">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center ${highlight ? "bg-[#FFD600] text-[#0A0C10]" : "bg-white border border-black/10"}`}>
          <Icon size={14} />
        </div>
        <div className={`text-[10px] uppercase tracking-[0.18em] font-mono ${highlight ? "text-neutral-400" : "text-neutral-500"}`}>step {i}</div>
      </div>
      <div className="mt-2 font-display font-semibold text-[14.5px]">{title}</div>
      <div className={`text-[12px] mt-0.5 ${highlight ? "text-neutral-400" : "text-neutral-600"}`}>{body}</div>
    </motion.div>
  );
}

/* ---------------- PILLAR 04 — Pricing + scale ---------------- */
function PillarFour() {
  return (
    <section id="pricing" className="py-24 lg:py-36 bg-[#FBFAF7]">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#FFB400] font-mono">[04] PRICING</div>
          <h2 className="mt-4 font-display font-semibold tracking-[-0.035em] text-[40px] sm:text-[56px] lg:text-[68px] leading-[1] mx-auto">
            Start free. <span className="italic font-medium" style={{ fontFamily: "'Instrument Serif', serif" }}>Scale on demand.</span>
          </h2>
          <p className="mt-5 text-[16.5px] text-neutral-700">Plans for individual tutors, busy professionals and entire coaching institutes. Top-ups for AI credits, storage, or extra student seats — buy when you need them.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { code: "trial", name: "Trial", price: 0, sub: "free, forever",
              items: ["5 students", "100 AI credits", "20 classes", "TS Platform live class"] },
            { code: "starter", name: "Starter", price: 19, sub: "/ month",
              items: ["25 students", "1,000 AI credits", "AI quiz + lesson plan", "Email reminders"] },
            { code: "professional", name: "Professional", price: 49, sub: "/ month", featured: true,
              items: ["100 students", "5,000 AI credits", "AI image + paper check", "Recurring classes", "Public teacher profile"] },
            { code: "institute", name: "Institute", price: 199, sub: "/ month",
              items: ["1,000 students", "25,000 AI credits", "Multi-teacher", "Central admin", "Priority support"] },
          ].map((p, i) => (
            <motion.div
              key={p.code} data-testid={`plan-${p.code}`}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }}
              transition={{ delay: i * 0.08, duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
              className={`relative rounded-2xl p-6 flex flex-col ${p.featured ? "bg-[#0A0C10] text-white" : "bg-white border border-black/8"}`}
            >
              {p.featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#FFB400] text-white text-[10px] font-semibold uppercase tracking-[0.15em]">Most popular</div>}
              <div className="font-display font-semibold text-[18px]">{p.name}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-display font-semibold tracking-[-0.035em] text-[52px] leading-none">${p.price}</span>
                <span className={`text-[13px] ${p.featured ? "text-neutral-400" : "text-neutral-500"}`}>{p.sub}</span>
              </div>
              <ul className={`mt-6 space-y-2 text-[13px] flex-1 ${p.featured ? "text-neutral-300" : "text-neutral-700"}`}>
                {p.items.map((x) => (
                  <li key={x} className="flex items-start gap-2"><CheckCircle2 size={14} className={p.featured ? "text-[#FFD600] mt-0.5 shrink-0" : "text-[#28C941] mt-0.5 shrink-0"} />{x}</li>
                ))}
              </ul>
              <Link
                to="/signup" data-testid={`plan-cta-${p.code}`}
                className={`mt-6 inline-flex items-center justify-center gap-2 font-semibold py-3 rounded-full text-[13.5px] transition-transform hover:scale-[1.02] ${p.featured ? "bg-[#FFD600] text-[#0A0C10]" : "bg-[#0A0C10] text-white"}`}
              >
                {p.price === 0 ? "Start free" : `Get ${p.name}`}
                <ArrowRight size={14} />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- AVATAR WALL ---------------- */
function AvatarWall() {
  // Use generated unique gradient backgrounds for "tutor avatars"
  const colors = [
    "from-[#FFB400] to-[#FFD600]", "from-[#002FA7] to-[#0DCAF0]", "from-[#28C941] to-[#FFD600]",
    "from-[#FFB400] to-[#A82A6E]", "from-[#0A0C10] to-[#4B5563]", "from-[#FFD600] to-[#FFB400]",
    "from-[#0DCAF0] to-[#28C941]", "from-[#A82A6E] to-[#0A0C10]", "from-[#FFB400] to-[#FFB400]",
    "from-[#002FA7] to-[#28C941]", "from-[#FFB400] to-[#002FA7]", "from-[#FFD600] to-[#A82A6E]",
  ];
  const rows = 6, perRow = 18;
  return (
    <section id="scale" className="py-24 lg:py-32 bg-white border-y border-black/8">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 text-center">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#FFB400] font-mono">[05] EARLY TUTORS</div>
        <h2 className="mt-4 font-display font-semibold tracking-[-0.035em] text-[36px] sm:text-[48px] lg:text-[60px] leading-[1.02] max-w-3xl mx-auto">
          Built with tutors — <span className="italic font-medium" style={{ fontFamily: "'Instrument Serif', serif" }}>shipped for tutors.</span>
        </h2>
      </div>
      <div className="mt-12 overflow-hidden" style={{ maskImage: "linear-gradient(to bottom, transparent, black 15%, black 85%, transparent)", WebkitMaskImage: "linear-gradient(to bottom, transparent, black 15%, black 85%, transparent)" }}>
        <div className="flex flex-col gap-2">
          {Array.from({ length: rows }).map((_, ri) => (
            <motion.div
              key={ri}
              animate={{ x: ri % 2 === 0 ? ["0%", "-50%"] : ["-50%", "0%"] }}
              transition={{ repeat: Infinity, duration: 45 + ri * 6, ease: "linear" }}
              className="flex gap-2 shrink-0"
            >
              {Array.from({ length: perRow * 2 }).map((__, i) => {
                const c = colors[(i + ri) % colors.length];
                return <div key={i} className={`w-10 h-10 rounded-full bg-gradient-to-br ${c} shrink-0 ring-2 ring-white`} />;
              })}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- FINAL CTA ---------------- */
function FinalCTA() {
  return (
    <section className="py-24 lg:py-36 bg-[#FBFAF7] relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(255,122,89,0.18), transparent 60%)" }} />
      </div>
      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
          className="font-display font-semibold tracking-[-0.04em] text-[44px] sm:text-[64px] lg:text-[88px] leading-[0.95]"
        >
          The next era of tuition <br />
          runs on <span className="italic font-medium" style={{ fontFamily: "'Instrument Serif', serif" }}>TeachingSoftware.ai</span>.
        </motion.h2>
        <motion.div
          initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15, duration: 0.6 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-3"
        >
          <Link to="/signup" data-testid="cta-start-free" className="bg-[#0A0C10] text-[#FBFAF7] font-semibold px-7 py-3.5 rounded-full text-[14px] hover:bg-black inline-flex items-center gap-2">
            Start for free <ArrowRight size={15} />
          </Link>
          <Link to="/login" className="bg-white border border-black/10 px-7 py-3.5 rounded-full text-[14px] font-medium">Talk to sales</Link>
        </motion.div>
      </div>
    </section>
  );
}
